import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    useMediaQuery,
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos"
import { useContext, useEffect, useRef } from "react"
import pin from "../../assets/red-map-pin-icon-png.webp"
import useAlert from "../../contexts/AlertContext"
import useEndereco from "../../contexts/EnderecoContext"
import useParametros from "../../contexts/ParametrosContext"
import SiteContext from "../../contexts/SiteContext"
import { useTextos } from "../../contexts/TextosContext"
import useGoogleMapsLoader from "../../hooks/useGoogleMaps"
import { ComumCep, RegistrarLogErro } from "../../services/functions"

const useStyles = makeStyles(theme => ({
    dialogContent: {
        display: "flex",
        textAlign: "center",
        height: "100%",
        [theme.breakpoints.down("xs")]: {
            padding: "0 0.5rem !important",
        },
    },
    contentContainer: {
        width: "80%",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto",
        padding: "0.5rem 0",
        [theme.breakpoints.down("sm")]: {
            width: "85%",
        },
        [theme.breakpoints.down("xs")]: {
            width: "100%",
        },
    },
    googleMapsContainer: {
        flex: 1,
        width: "100%",
        aspectRatio: "12/5",
        "& > div": { height: "100%" },
        [theme.breakpoints.down("sm")]: {
            aspectRatio: "2/1",
        },
        [theme.breakpoints.down("xs")]: {
            aspectRatio: "4/3",
        },
    },
    buttonContainer: {
        display: "flex",
        width: "100%",
        gap: "clamp(0.5rem, 5%, 1rem)",
        "& > button": {
            flex: 1,
        },
        [theme.breakpoints.down("xs")]: {
            flexDirection: "column-reverse",
        },
    },
    confirmarButton: {
        backgroundColor: `#28a745`,
        color: "white",
        "&:hover": {
            backgroundColor: "#28a745",
            filter: "brightness(1.05)",
        },
    },
}))

export default function DialogGoogleMaps({ DialogConfirmacaoinformacoes = () => {} }) {
    const {
        aplicativoDados,

        carrinho,

        estabelecimentoAtual,

        setLoading,
    } = useContext(SiteContext)

    const classes = useStyles()
    const parametros = useParametros()
    const textos = useTextos()
    const alertStart = useAlert()

    const {
        setNovoEndereco,
        selecionarRetiradaNoLocal,

        permiteRetiradaNoLocal,
        abrirGoogleMaps,

        fecharCadastroGoogleMaps,
        abrirCadastroEndereco,
        fecharCadastroCEP,
    } = useEndereco()

    const usarGoogleMapsAPI = aplicativoDados.tipoSelecaoEndereco === 1
    if (abrirGoogleMaps && !usarGoogleMapsAPI)
        throw new Error("Google Maps opened without system parameter")

    const googleMapRef = useRef(null)
    const listenerRef = useRef(null)
    const markerRef = useRef(null)
    const placeRef = useRef(null)

    const loader = useGoogleMapsLoader()

    const checarCepGoogleMaps = async endereco => {
        try {
            let valorCep = endereco.address_components
                .find(addressComponent => addressComponent.types.includes("postal_code"))
                ?.long_name.replace(/-/g, "")
                .replace(/ /g, "")
                .replace(/\//g, "")

            if (valorCep?.length === 8) {
                setLoading(true)
                const response = await ComumCep(valorCep, aplicativoDados)

                if (response.retornoErro) {
                    setLoading(false)
                    alertStart(response.mensagem, "error")
                    return
                }

                if (response.bairroEspecifico || response.bairros?.length >= 1) {
                    setNovoEndereco({
                        ...response,
                        bairroEspecifico: true,
                        cep: valorCep,
                        logradouro: endereco.address_components?.find(addressComponent =>
                            addressComponent.types.includes("route")
                        )?.long_name,
                        numero: endereco.address_components?.find(addressComponent =>
                            addressComponent.types.includes("street_number")
                        )?.long_name,
                        localidade: endereco.address_components?.find(addressComponent =>
                            addressComponent.types.includes("administrative_area_level_2")
                        )?.long_name,
                        uf: endereco.address_components?.find(addressComponent =>
                            addressComponent.types.includes("administrative_area_level_1")
                        )?.short_name,
                        latitude: endereco.geometry?.location.lat().toFixed(7),
                        longitude: endereco.geometry?.location.lng().toFixed(7),
                    })
                } else if (response.id || response.logradouro) {
                    response.numero = endereco.address_components?.find(addressComponent =>
                        addressComponent.types.includes("street_number")
                    )?.long_name
                    setNovoEndereco({
                        ...response,
                        logradouro: endereco.address_components?.find(addressComponent =>
                            addressComponent.types.includes("route")
                        )?.long_name,
                        bairroEspecifico: false,
                        latitude: endereco.geometry?.location.lat().toFixed(7),
                        longitude: endereco.geometry?.location.lng().toFixed(7),
                    })
                } else {
                    alertStart("Não foi possível identificar seu CEP", "error")
                }
            } else {
                setNovoEndereco({
                    logradouro: endereco.address_components?.find(addressComponent =>
                        addressComponent.types.includes("route")
                    )?.long_name,
                    numero: endereco.address_components?.find(addressComponent =>
                        addressComponent.types.includes("street_number")
                    )?.long_name,
                    bairro: endereco.address_components?.find(addressComponent =>
                        addressComponent.types.includes("sublocality_level_1")
                    )?.long_name,
                    localidade: endereco.address_components?.find(addressComponent =>
                        addressComponent.types.includes("administrative_area_level_2")
                    )?.long_name,
                    uf: endereco.address_components?.find(addressComponent =>
                        addressComponent.types.includes("administrative_area_level_1")
                    )?.short_name,
                    bairroEspecifico: aplicativoDados.tipoEntregaBairro === 1,
                    cep: valorCep,
                    latitude: endereco.geometry?.location.lat(),
                    longitude: endereco.geometry?.location.lng(),
                })
            }
            abrirCadastroEndereco()
            fecharCadastroCEP()
            setLoading(false)
        } catch (error) {
            RegistrarLogErro(error, "checarCepGoogleMaps", endereco)
            alertStart("Procure os desenvolvedores! Erro: " + error.message, "error")
            setLoading(false)
        }
    }

    useEffect(() => {
        if (abrirGoogleMaps && loader) {
            loader.load().then(google => {
                const input = document.getElementById("search-input")
                let inputBox

                const temCoordenada =
                    Number(estabelecimentoAtual.latitude) && Number(estabelecimentoAtual.longitude)

                const mapCenter = temCoordenada
                    ? {
                          lat: Number(estabelecimentoAtual.latitude),
                          lng: Number(estabelecimentoAtual.longitude),
                      }
                    : { lat: -12.720151, lng: -50.43879 }

                const mapElement = googleMapRef.current

                // mapElement.style.width = "100%"
                // mapElement.style.height = "65vh"

                const getClientLocation = () => {
                    return new Promise((resolve, reject) => {
                        if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(resolve, reject)
                        } else {
                            reject(new Error("Geolocalização não é suportada pelo seu navegador."))
                        }
                    })
                }

                const initializeMap = clientLocation => {
                    const mapCenter =
                        clientLocation ||
                        (temCoordenada
                            ? {
                                  lat: Number(estabelecimentoAtual.latitude),
                                  lng: Number(estabelecimentoAtual.longitude),
                              }
                            : { lat: -12.720151, lng: -50.43879 })

                    const map = new google.maps.Map(mapElement, {
                        center: mapCenter,
                        zoom: clientLocation ? 18 : 4,
                        // mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: false,
                        zoomControl: false,
                        // disableDefaultUI: true,
                        gestureHandling: "greedy",
                    })

                    const pinDiv = document.createElement("div")
                    pinDiv.style.position = "absolute"
                    pinDiv.style.transform = "translate(-50%, -100%)"
                    pinDiv.style.zIndex = 1000
                    pinDiv.innerHTML = `<img src=${pin} alt='Pin' style='width: 70px; height: 70px;' />`
                    mapElement.appendChild(pinDiv)

                    const centerMapPin = () => {
                        const center = map.getCenter()
                        pinDiv.style.left = `${map.getDiv().offsetWidth / 2}px`
                        pinDiv.style.top = `${map.getDiv().offsetHeight / 2}px`
                    }
                    centerMapPin()

                    map.addListener("dragend", async () => {
                        const center = map.getCenter()
                        const newLocation = {
                            lat: center.lat(),
                            lng: center.lng(),
                        }
                        console.log("Nova localização do marcador:", newLocation)

                        try {
                            const geocoder = new google.maps.Geocoder()
                            const response = await geocoder.geocode({ location: newLocation })

                            if (response.results[0]) {
                                const endereco = response.results[0]
                                input.value = endereco.formatted_address
                                placeRef.current = endereco
                            } else {
                                console.error(
                                    "Nenhum resultado encontrado para a nova localização."
                                )
                            }
                        } catch (error) {
                            console.error("Erro ao tentar recuperar o endereço:", error)
                        }
                    })

                    google.maps.importLibrary("places").then(googlePlacesLibrary => {
                        const codigoPais = parametros.PUBLIC_CODIGO_PAIS ?? "br"

                        const options = {
                            bounds: temCoordenada
                                ? {
                                      north: mapCenter.lat + 0.2,
                                      south: mapCenter.lat - 0.2,
                                      east: mapCenter.lng + 0.2,
                                      west: mapCenter.lng - 0.2,
                                  }
                                : { east: 180, west: -180, north: 90, south: -90 }, // Todo o mapa
                            strictBounds: false,
                            fields: ["formatted_address", "geometry", "address_components"],
                            componentRestrictions: { country: codigoPais },
                        }

                        inputBox = new googlePlacesLibrary.Autocomplete(input, options)

                        const handleAdressSelection = () => {
                            placeRef.current = inputBox.getPlace()

                            if (!placeRef.current.geometry) return

                            if (placeRef.current.geometry.viewport) {
                                map.fitBounds(placeRef.current.geometry.viewport)
                            } else {
                                map.setCenter(placeRef.current.geometry.location)
                                map.setZoom(16)
                            }

                            pinDiv.style.left = `${map.getDiv().offsetWidth / 2}px`
                            pinDiv.style.top = `${map.getDiv().offsetHeight / 2}px`
                        }

                        listenerRef.current = inputBox.addListener(
                            "place_changed",
                            handleAdressSelection
                        )
                    })
                }

                getClientLocation()
                    .then(async position => {
                        const clientLocation = {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        }

                        initializeMap(clientLocation)

                        const geocoder = new google.maps.Geocoder()
                        try {
                            const response = await geocoder.geocode({
                                location: clientLocation,
                            })
                            if (response.results[0]) {
                                const endereco = response.results[0]
                                input.value = endereco.formatted_address
                                placeRef.current = endereco
                            } else {
                                console.error(
                                    "Nenhum resultado encontrado para a localização atual."
                                )
                            }
                        } catch (error) {
                            console.error("Erro ao tentar recuperar o endereço:", error)
                        }
                    })
                    .catch(error => {
                        console.error("Erro ao obter a localização do cliente:", error)
                        initializeMap()
                    })
            })
        }

        return () => listenerRef.current?.remove()
    }, [abrirGoogleMaps, googleMapRef.current, loader])

    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))

    const handleClose = () => {
        fecharCadastroGoogleMaps()
    }

    const handleRetiradaNoLocal = () => {
        selecionarRetiradaNoLocal()

        if (carrinho.pedido?.itens?.length) {
            DialogConfirmacaoinformacoes(true)
        }
    }

    const handleSubmit = e => {
        e.preventDefault()

        if (!placeRef.current) {
            alertStart(textos.digiteEndereco, "warning")
            return
        }

        // TODO: Otimizar e talvez trocar para removeAcento
        const stringSemAcento = str => {
            return str
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
        }

        if (
            // Verificar se a pessoa digitou somente o nome da cidade no campo de busca do Maps
            placeRef?.current?.address_components.length <= 3 ||
            (placeRef?.current?.address_components &&
                placeRef?.current?.address_components[0] &&
                placeRef?.current?.address_components[1] &&
                stringSemAcento(placeRef?.current?.address_components[0].long_name) ===
                    stringSemAcento(placeRef?.current?.address_components[1].long_name) &&
                stringSemAcento(placeRef?.current?.address_components[0].short_name) ===
                    stringSemAcento(placeRef?.current?.address_components[1].short_name))
        ) {
            alertStart(textos.digiteEnderecoCompleto, "warning")
            return
        }

        checarCepGoogleMaps(placeRef.current)
        abrirCadastroEndereco()
        handleClose()
        setLoading(true)
    }

    return (
        <Dialog
            open={abrirGoogleMaps}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="md"
            onClose={handleClose}
        >
            {!fullScreen ? <img src={aplicativoDados.urlLogo} className="imgModal" /> : null}
            <DialogTitle>
                <IconButton className="setaVoltarModal" onClick={handleClose}>
                    <ArrowForwardIosIcon />
                </IconButton>
                {textos.digiteEndereco}
            </DialogTitle>
            <form onSubmit={handleSubmit} style={{ height: "100%" }}>
                <DialogContent className={classes.dialogContent}>
                    <div className={classes.contentContainer}>
                        <TextField
                            id="search-input"
                            autoFocus
                            type="text"
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder={textos.digiteEndereco}
                        />
                        <div className={classes.googleMapsContainer}>
                            <div ref={googleMapRef} />
                        </div>
                        <div className={classes.buttonContainer}>
                            {permiteRetiradaNoLocal ? (
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleRetiradaNoLocal}
                                >
                                    {textos.retirarNoLocal}
                                </Button>
                            ) : null}
                            <Button
                                size="small"
                                type="submit"
                                className={classes.confirmarButton}
                                variant="contained"
                            >
                                {textos.confirmar}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </form>
        </Dialog>
    )
}
